import { useEffect, useState, type ReactElement } from 'react';
import { emberApi, isAuthRequired, type ConversationMessage, type OwnerEmberDetailResponse } from '../api';
import { trackFailed } from '../../../../../shared-web/track';
import { provenanceLine } from '../format';
import { navigate } from '../router';
import { useRequireAuth } from '../use-require-auth';
import { PageBack } from '../components/PageBack';
import { PageFailure } from '../components/PageFailure';
import { PageHome } from '../components/PageHome';
import { PageLoading } from '../components/PageLoading';
import './EmberTranscript.css';

export interface EmberTranscriptPageProps {
  emberId: string;
  path: string;
}

/**
 * S13a — the telling an Ember was written from (§4.13, 2026-09-04).
 *
 * The Ember is the finished thing; this is the evening it came out of, kept
 * so its owner can read back what they actually said. It is owner-only and
 * always has been (§4.5: the transcript is private source material, not a
 * second unedited version of the Ember) — the page adds a door for the person
 * whose words these are, and no door for anybody else.
 *
 * It reads as a record rather than a chat: no bubbles, no composer, no
 * scroll-to-bottom. Speaker, then what they said, down the page.
 *
 * Two reads, because the address is the Ember and the words hang off the
 * conversation: the Ember detail carries the title, the receipt, and the
 * conversation's id; that conversation carries the turns.
 */
export function EmberTranscriptPage({ emberId, path }: EmberTranscriptPageProps): ReactElement {
  const status = useRequireAuth(path);
  const [ember, setEmber] = useState<OwnerEmberDetailResponse['ember'] | null>(null);
  const [messages, setMessages] = useState<ConversationMessage[] | null>(null);
  const [missing, setMissing] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (status !== 'authenticated') return undefined;
    let alive = true;
    emberApi.ownerEmberDetail(emberId)
      .then((detail) => {
        if (!alive) return;
        setEmber(detail.ember);
        const conversationId = detail.ember.source_conversation_id;
        // An Ember always has a source conversation; a row without one is a
        // lineage that was cleaned up underneath it, and there is nothing to
        // read back.
        if (!conversationId) { setMissing(true); return; }
        emberApi.getConversation(conversationId)
          .then((c) => { if (alive) setMessages(c.messages); })
          .catch((err: unknown) => {
            if (!alive) return;
            if (isAuthRequired(err)) navigate(`/auth?returnTo=${encodeURIComponent(path)}`, { replace: true });
            else {
              setFailed(true);
              trackFailed('ember_transcript_load', 'conversation_failed', { ember_id: emberId });
            }
          });
      })
      .catch((err: unknown) => {
        if (!alive) return;
        if (isAuthRequired(err)) navigate(`/auth?returnTo=${encodeURIComponent(path)}`, { replace: true });
        else if ((err as { status?: number }).status === 404) {
          setMissing(true);
          trackFailed('ember_transcript_load', 'not_yours', { ember_id: emberId });
        } else {
          setFailed(true);
          trackFailed('ember_transcript_load', 'load_failed', { ember_id: emberId });
        }
      });
    return () => { alive = false; };
  }, [emberId, status, path]);

  if (status !== 'authenticated') return <div className="ember-page-quiet">One moment…</div>;
  if (missing) return <PageFailure title="This conversation isn't yours to see" logState="not_yours" />;
  if (failed) return <PageFailure title="Couldn't load this conversation" detail="Refresh to try again." logState="load_failed" />;
  if (!ember || !messages) return <PageLoading />;

  const receipt = provenanceLine(ember.provenance);

  return (
    <div className="ember-container ember-reading transcript-page">
      <div className="transcript-toolbar">
        <PageBack to={`/me/embers/${ember.id}`} className="transcript-back" fallbackLabel="Ember" />
        <div className="ember-toolbar-actions">
          <PageHome />
        </div>
      </div>
      <header className="transcript-head">
        {receipt && <p className="transcript-receipt">{receipt}</p>}
        <h1 className="transcript-title">Original conversation</h1>
        <p className="transcript-sub" dir="auto">
          {ember.title || 'Untitled Ember'}
        </p>
        {/* The original is looser and more spoken than the Ember it became;
            saying so up front is what keeps a person from reading their own
            evening as a mess. And it is theirs alone — the one other thing to
            know before reading (2026-09-04 second pass). */}
        <p className="transcript-note">This is what you said before Ember shaped it. Only you can open it.</p>
      </header>
      <article className="transcript-turns">
        {messages.map((m) => (
          <div key={m.turn_index} className={`transcript-turn is-${m.role}`}>
            <p className="transcript-speaker">{m.role === 'user' ? 'You' : 'Ember'}</p>
            {m.content && <p className="transcript-said" dir="auto">{m.content}</p>}
            {(m.attachments?.length ?? 0) > 0 && (
              <div className="transcript-photos">
                {m.attachments?.map((a) => (
                  <img key={a.media_id} src={a.url} alt="Photo sent with this telling" loading="lazy" decoding="async" />
                ))}
              </div>
            )}
          </div>
        ))}
      </article>
    </div>
  );
}
